import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Play, Zap, BarChart3, MessageSquareText, EyeOff } from "lucide-react";
import { useBrandsStore } from "@/stores/brands";
import { usePromptsStore } from "@/stores/prompts";
import { useDefaultModelSeed, useModelsStore } from "@/stores/models";
import { useComparisonsStore } from "@/stores/comparisons";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { BrandSelector } from "./BrandSelector";
import { ModelSelector } from "./ModelSelector";
import { ComparisonResults } from "./ComparisonResults";
import { RunQueryProgressModal } from "./RunQueryProgressModal";
import { Badge } from "@/components/ui/badge";
import { LeakWarning } from "@/components/ui/leak-warning";
import { PromptSelect } from "@/components/prompts/PromptSelect";
import { cn } from "@/lib/utils";
import { useBlindQueryLeakCheck } from "@/lib/useBlindQueryLeakCheck";
import type { QueryMode } from "../../../types";

export function CompareView() {
  const navigate = useNavigate();
  const fetchBrands = useBrandsStore((s) => s.fetch);
  const fetchPrompts = usePromptsStore((s) => s.fetch);
  const fetchModels = useModelsStore((s) => s.fetch);
  const prompts = usePromptsStore((s) => s.prompts);
  const brands = useBrandsStore((s) => s.brands);

  const runComparison = useComparisonsStore((s) => s.runComparison);
  const runBatch = useComparisonsStore((s) => s.runBatch);
  const clearActive = useComparisonsStore((s) => s.clearActive);
  const activeComparison = useComparisonsStore((s) => s.activeComparison);
  const activeRunGroup = useComparisonsStore((s) => s.activeRunGroup);

  const [selectedBrands, setSelectedBrands] = useState<Set<number>>(new Set());
  const [selectedPrompt, setSelectedPrompt] = useState("");
  const [selectedModels, setSelectedModels] = useState<Set<string>>(new Set());
  const [repeatCount, setRepeatCount] = useState(1);
  // /app/compare?mode=blind (the Explore "Blind query" card) starts in blind mode.
  const [searchParams] = useSearchParams();
  const [queryMode, setQueryMode] = useState<QueryMode>(searchParams.get("mode") === "blind" ? "blind_query" : "standard");
  const [error, setError] = useState("");
  const [isProgressDismissed, setIsProgressDismissed] = useState(false);

  useEffect(() => {
    fetchBrands();
    fetchPrompts();
    fetchModels();
  }, []);

  useDefaultModelSeed(true, (ids) => setSelectedModels((prev) => (prev.size > 0 ? prev : new Set(ids))));

  useEffect(() => {
    if (activeComparison?.status === "completed" && activeComparison.result) {
      const resultId = activeComparison.result.id;
      clearActive();
      navigate(`/app/compare/results/${resultId}`);
    }
  }, [activeComparison?.status, activeComparison?.result?.id, clearActive, navigate]);

  const pollingProgress = activeComparison?.status === "polling"
    ? ({ kind: "single" } as const)
    : activeRunGroup?.status === "polling"
      ? ({ kind: "run-group", completedCount: activeRunGroup.completedCount, expectedCount: activeRunGroup.expectedCount } as const)
      : null;

  useEffect(() => {
    if (pollingProgress) setIsProgressDismissed(false);
  }, [pollingProgress?.kind, activeComparison?.id, activeRunGroup?.groupId]);

  const validate = () => {
    const minBrands = queryMode === "blind_query" ? 0 : 1;

    if (queryMode === "blind_query" && brands.length === 0) {
      return "Add at least one workspace brand to track after a Blind Query.";
    }

    if (selectedBrands.size < minBrands) return `Select at least ${minBrands} brand${minBrands === 1 ? "" : "s"} to evaluate`;
    if (selectedModels.size === 0) return "Select at least 1 model";
    return "";
  };

  const handleRun = async () => {
    if (!selectedPrompt) {
      setError("Select a prompt");
      return;
    }
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    if (hasBlindQueryPromptLeak) {
      setError("Remove tracked brand names from the prompt before running a Blind Query.");
      return;
    }
    setError("");
    clearActive();

    const brandIds = Array.from(selectedBrands);
    const models = Array.from(selectedModels);
    const promptId = parseInt(selectedPrompt);

    if (models.length > 1) {
      await runBatch({ brand_ids: brandIds, prompt_ids: [promptId], models, query_mode: queryMode });
    } else {
      await runComparison({
        brand_ids: brandIds,
        prompt_id: promptId,
        model: models[0]!,
        repeat: repeatCount,
        query_mode: queryMode,
      });
    }
  };

  const handleBatch = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    if (prompts.length === 0) {
      setError("No prompts available");
      return;
    }
    setError("");
    clearActive();

    await runBatch({
      brand_ids: Array.from(selectedBrands),
      prompt_ids: prompts.map((p) => p.id),
      models: Array.from(selectedModels),
      query_mode: queryMode,
    });
  };

  const selectedPromptObj = prompts.find(p => String(p.id) === selectedPrompt);
  const isSentiment = selectedPromptObj?.type === "sentiment";
  const isBlindQuery = queryMode === "blind_query";
  const minBrands = isBlindQuery ? 0 : 1;
  const selectedBrandNames = brands.filter(b => selectedBrands.has(b.id)).map(b => b.name);
  const autoTrackAllBrands = isBlindQuery && selectedBrands.size === 0;
  const leakCheckBrandNames = autoTrackAllBrands ? brands.map(b => b.name) : selectedBrandNames;
  const { brandNamesInPrompt, hasLeak: hasBlindQueryPromptLeak } = useBlindQueryLeakCheck({
    enabled: isBlindQuery,
    promptTemplate: selectedPromptObj?.template,
    brandNames: leakCheckBrandNames,
  });
  const canTrackBlindQuery = !isBlindQuery || brands.length > 0;
  const isReady = selectedBrands.size >= minBrands && selectedModels.size >= 1 && Boolean(selectedPrompt) && !hasBlindQueryPromptLeak && canTrackBlindQuery;

  return (
    <div className="space-y-8">
      {/* Configuration Panel */}
      <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
        {/* Left Column -- Main Config */}
        <div className="space-y-6">
          {/* Brands */}
          <Card>
            <CardHeader>
                <CardTitle>Brands</CardTitle>
                <CardDescription>
                  {isBlindQuery
                    ? "Select tracked brands or leave empty to track every workspace brand"
                    : "Select at least 1 brand to evaluate"}
                </CardDescription>
            </CardHeader>
            <CardContent>
              <BrandSelector
                selected={selectedBrands}
                onChange={setSelectedBrands}
              />
              {isBlindQuery && (
                <p className="mt-3 text-[12px] leading-relaxed text-slate-500">
                  {autoTrackAllBrands
                    ? "All workspace brands will be tracked after the response. None are sent to the model."
                    : "Selected tracked brands are monitored after the response. They are not sent to the model."}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Models */}
          <Card>
            <CardHeader>
              <CardTitle>AI Models</CardTitle>
              <CardDescription>Select which models evaluate the brands</CardDescription>
            </CardHeader>
            <CardContent>
              <ModelSelector
                selected={selectedModels}
                onChange={setSelectedModels}
              />
            </CardContent>
          </Card>
        </div>

        {/* Right Column -- Prompt + Settings + Actions */}
        <div className="space-y-6">
          {/* Prompt Selection */}
          <Card>
            <CardHeader>
              <CardTitle>Prompt</CardTitle>
              <CardDescription>Choose a scoring or sentiment prompt</CardDescription>
            </CardHeader>
            <CardContent>
              <PromptSelect
                value={selectedPrompt}
                onChange={(e) => setSelectedPrompt(e.target.value)}
                prompts={prompts}
              />

              {/* Type indicator + dimensions */}
              {selectedPromptObj && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-1.5">
                    {isSentiment ? (
                      <MessageSquareText className="w-3.5 h-3.5 text-brand-500" />
                    ) : (
                      <BarChart3 className="w-3.5 h-3.5 text-slate-400" />
                    )}
                    <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.04em]">
                      {isSentiment ? "Sentiment query" : "Scoring template"}
                    </span>
                  </div>
                  {isSentiment && (
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                      Two-step analysis: the AI responds naturally, then we extract recommendation, sentiment, and visibility scores.
                    </p>
                  )}
                  {isBlindQuery && (
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                      Blind query mode sends only the prompt text. No tracked brand names are included in the prompt.
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {selectedPromptObj.dimensions.map(d => (
                      <span key={d} className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                        {d}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {isBlindQuery && (
                <LeakWarning brandNames={brandNamesInPrompt} context="blind query" compact className="mt-3" />
              )}
            </CardContent>
          </Card>

          {/* Query Mode */}
          <Card>
            <CardHeader>
              <CardTitle>Mode</CardTitle>
              <CardDescription>Choose how tracked brands are handled</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {([
                { id: "standard" as QueryMode, label: "Standard", description: "Send selected brands to the model for direct scoring." },
                { id: "blind_query" as QueryMode, label: "Blind Query", description: "Send no tracked brands; analyze mentions after the response." },
              ]).map(mode => (
                <button
                  key={mode.id}
                  type="button"
                  onClick={() => setQueryMode(mode.id)}
                  className={cn(
                    "w-full rounded-md border p-3 text-left transition-all duration-150 cursor-pointer",
                    queryMode === mode.id
                      ? "border-brand-600 bg-brand-50 ring-1 ring-brand-600/20"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className={cn("text-[13px] font-semibold", queryMode === mode.id ? "text-brand-950" : "text-slate-600")}>{mode.label}</span>
                    {mode.id === "blind_query" && <Badge variant="muted" className="gap-1"><EyeOff className="w-3 h-3" />Blind</Badge>}
                  </div>
                  <p className="mt-1 text-[11px] leading-relaxed text-slate-400">{mode.description}</p>
                </button>
              ))}
            </CardContent>
          </Card>

          {/* Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <Label htmlFor="repeat-count">Repeat count</Label>
              <Input
                id="repeat-count"
                type="number"
                min={1}
                max={10}
                value={repeatCount}
                onChange={(e) =>
                  setRepeatCount(
                    Math.min(10, Math.max(1, parseInt(e.target.value) || 1))
                  )
                }
              />
              <p className="text-[11px] text-slate-400 mt-1.5">
                Run multiple times and average results
              </p>
            </CardContent>
          </Card>

          {/* Error */}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
              <p className="text-[13px] text-red-700">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-2">
            <Button
              onClick={handleRun}
              size="lg"
              className="w-full"
              disabled={!isReady}
              title={hasBlindQueryPromptLeak ? "Remove tracked brand names from the prompt before running a Blind Query." : undefined}
            >
              <Play className="w-4 h-4 mr-2" />
              {isBlindQuery ? "Run Blind Query" : "Run Evaluation"}
            </Button>
            <Button
              variant="secondary"
              onClick={handleBatch}
              size="lg"
              className="w-full"
              disabled={selectedBrands.size < 1 || selectedModels.size < 1 || isBlindQuery}
              title={isBlindQuery ? "Batch all prompts is available in Standard mode." : undefined}
            >
              <Zap className="w-4 h-4 mr-2" />
              Batch All Prompts
            </Button>
          </div>
        </div>
      </div>

      {/* Results */}
      {pollingProgress && (
        <RunQueryProgressModal
          open={!isProgressDismissed}
          onOpenChange={(open) => setIsProgressDismissed(!open)}
          progress={pollingProgress}
        />
      )}
      <ComparisonResults loadingPresentation="none" />
    </div>
  );
}
